import React from 'react';
import type { TFunction } from 'i18next';

import { fromDateTimeType } from '@/js/libs/api-helpers';
import { Link, TableCellContentType } from '@/js/libs/ui/src/V2';

import { ROUTER } from 'src/AppRouter';
import { DATE_TIME_FORMAT } from 'src/constants';
import type { Column } from '../../../types';
import { ActivityCell, ArchivedActionsCell, OutcomeCell, PriorityCell, LabelsCell } from '../../cells';

export const useColumns = (t: TFunction) =>
  React.useMemo<Column[]>(
    () => [
      {
        name: 'id',
        title: t('common:ID'),
        component: ({ item }) =>
          item.id && <Link to={ROUTER['/sales/tasks/details/:taskId'].path({ taskId: item.id.toString() })}>{item.id.toString()}</Link>,
        sortable: true,
      },
      {
        name: 'outcome',
        title: t('serviceDashboard:OUTCOME'),
        component: OutcomeCell,
        sortable: true,
        dataTestId: 'outcome-header',
      },
      {
        name: 'priority',
        title: t('serviceDashboard:PRIORITY'),
        component: PriorityCell,
        dataTestId: 'priority-header',
      },
      {
        name: 'activity',
        title: t('serviceDashboard:ACTIVITY'),
        component: ActivityCell,
      },
      {
        name: 'account',
        title: t('common:ACCOUNT'),
        component: ({ item }) =>
          item.account?.id && (
            <Link to={ROUTER['/crm/accounts/:accountId'].path({ accountId: item.account.id.toString() })}>{item.account?.displayName}</Link>
          ),
        breakWord: true,
      },
      {
        name: 'createdDate',
        title: t('common:CREATED'),
        component: ({ item }) => fromDateTimeType(item.createdDate)?.local().format(DATE_TIME_FORMAT),
        sortable: true,
        dataTestId: 'created-header',
      },
      {
        name: 'leadSource.name',
        title: t('serviceDashboard:TASK_SOURCE'),
        component: ({ item }) => item.leadSource.name,
        sortable: true,
        breakWord: true,
        dataTestId: 'lead-source-header',
      },
      {
        name: 'taskTopic',
        title: t('serviceDashboard:TASK_TOPIC'),
        component: ({ item }) => item.taskTopic?.name,
        sortable: true,
        breakWord: true,
        dataTestId: 'task-topic-header',
      },
      {
        name: 'labels',
        title: t('common:LABELS'),
        component: LabelsCell,
        breakWord: true,
      },
      {
        name: 'creator',
        title: t('serviceDashboard:CREATOR'),
        component: ({ item }) => item.creator?.name,
        breakWord: true,
      },
      {
        name: 'accountSalesName',
        title: t('common:SALES'),
        component: ({ item }) => item.accountSalesName,
        sortable: true,
        breakWord: true,
        dataTestId: 'sales-header',
      },
      {
        name: 'actions',
        component: ArchivedActionsCell,
        contentType: TableCellContentType.ACTION,
      },
    ],
    [t],
  );
